<template>
  <div
    class="message"
    v-bind:class="{ 'align-right': fromUser, 'align-left': !fromUser }"
    v-if="!message.hideMessage"
    ref="message"
  >
    <div class="avatar-wrapper" v-if="!fromUser && hasContent">
      <template v-if="showFromName">
        <img
          class="avatar"
          :src="botAvatarUrl"
          v-if="botAvatarUrl"
          @error="onAvatarError"
        />
        <div
          class="avatar avatar-initials"
          :style="{ background: themeStore.theme.primaryColor }"
          v-else
        >{{ botAvatarInitial }}</div>
      </template>
    </div>
    <div class="from-name" v-if="showFromName && hasContent">
      {{ fromDisplayName }}
      <Timestamp :datetime="message.timestamp"></Timestamp>
    </div>
    <!-- Sent attachments (bot mode, user messages) -->
    <div class="sent-attachments" v-if="fromUser && message.attachments && message.attachments.length">
      <template v-for="(att, index) in message.attachments" :key="index">
        <span class="sent-attachment-chip">
          <span :class="'bt-icon ' + sentAttachmentIcon(att)"></span>
          <span class="sent-attachment-name">{{ att.filename || att.url }}</span>
        </span>
      </template>
    </div>
    <div class="message-text" v-if="message.message || message.streamingContent">
      <span v-html="displayContent"></span>
      <span v-if="message.isStreaming" class="streaming-cursor">|</span>
      <p v-if="message.triggers?.escalateCase">
        <a href="" @click.prevent="goToChatWithHuman"
        to="/chat/chat-with-human"
          >Chat with a representative</a
        >
      </p>

      <!-- Rating and Sources Section for Bot Messages -->
      <div v-if="message.resource === 'ovation-bot' && !message.isStreaming && (message.traceId || (message.sources && message.sources.length))" class="message-footer">
        <div class="rating-sources-container">
          <!-- Rating Buttons (only show if traceId exists) -->
          <div v-if="message.traceId" class="rating-buttons">
            <button 
              class="rating-btn" 
              :class="{ 'selected': userRating === 'up' }"
              @click="onThumbsUp"
              aria-label="Thumbs up"
              :disabled="ratingSubmitted"
            >
              <span class="bt-icon bt-icon-thumb-up"></span>
            </button>
            <button 
              class="rating-btn" 
              :class="{ 'selected-down': userRating === 'down' }"
              @click="onThumbsDown"
              aria-label="Thumbs down"
              :disabled="ratingSubmitted"
            >
              <span class="bt-icon bt-icon-thumb-down"></span>
            </button>
          </div>

          <!-- Sources Accordion (only show if sources exist) -->
          <div class="accordion" v-if="message.sources && message.sources.length">
            <button class="accordion-toggle" @click="toggleSources">
              Sources ({{ message.sources.length }}) <span class="arrow" :class="{ 'arrow-down': showSources }">▸</span>
            </button>
          </div>
        </div>

        <!-- Sources Content -->
        <transition name="accordion">
          <div class="accordion-content" v-show="showSources && message.sources && message.sources.length">
            <div v-for="(source, index) in message.sources" :key="index" class="source-item">
              <a :href="getSourceUrl(source)" target="_blank">
                <span>{{ getSourceUrl(source) }}</span>
                <span class="bt-icon bt-icon-move"></span>
              </a>
            </div>
          </div>
        </transition>

        <!-- Feedback Comment Box (shown after thumbs down) -->
        <transition name="fadeIn">
          <div v-if="showFeedbackBox" class="feedback-box">
            <textarea 
              v-model="feedbackComment"
              placeholder="What was bad about this response?"
              rows="3"
              class="feedback-textarea"
            ></textarea>
            <button 
              @click="submitFeedback"
              class="feedback-submit-btn"
              :disabled="!feedbackComment.trim() || submittingFeedback"
            >
              <span class="bt-icon bt-icon-send-1"></span>
              Submit Feedback
            </button>
          </div>
        </transition>
      </div>
      
    </div>
    <div class="message-attachments">
      <div
        class="image-wrapper"
        v-if="message.hasImage"
        @click="goToImagePreview(message.attachmentUrl)"
      >
        <img :src="message.attachmentPreview" class="image" />
      </div>
      <user-identification
        style="right: 5%"
        v-if="message.showUserIdentification && !memberStore.member.members_id"
      ></user-identification>
      <k-b-preview
        v-if="message.hasKb"
        :id="message.kbId"
        :title="message.kbTitle"
        :preview="message.kbPreview"
      ></k-b-preview>
      <k-b-preview-list
        v-if="message.kbList && message.kbList.length > 0"
        :list="message.kbList"
      ></k-b-preview-list>
      <div v-if="message.ratingActions">
        <span v-for="(action, index) in message.ratingActions" :key="index">
          <rating-button
            :label="action.lbl"
            @click="onRatingClick(action, message, $event)"
            :color="themeStore.theme.primaryHoverColor"
            :selected="action.selected"
          ></rating-button>
        </span>
      </div>
      <div v-if="message.actions">
        <span v-for="(action, index) in message.actions" :key="index">
          <action-button
            :label="action.lbl"
            @click="onActionClick(action, message, $event)"
            :color="themeStore.theme.primaryHoverColor"
            :selected="action.selected"
            :disabled="message.botActionSelected"
          ></action-button>
        </span>
      </div>
      <div
        class="file-attachment"
        v-if="message.hasAttachment && !message.hasImage"
      >
        <a
          :href="message.attachmentUrl"
          target="_blank"
          class="file-attachment-link"
          download
        >
          <div class="file-wrapper">
            <span :class="iconClasses"></span>
            <span class="file-name-wrapper">
              <div class="file-name">{{ message.attachmentName }}</div>
              <div class="file-size">{{ message.attachmentSize }}</div>
            </span>
          </div>
        </a>
      </div>
    </div>
    <div v-if="message.lastBotMessage" class="transfer-message">
      Transferring the conversation. A representative will be with your shortly
    </div>
  </div>
</template>

<script>
import ActionButton from '@/components/ActionButton'
import RatingButton from '@/components/RatingButton'
import chatService from '@/services/chatService'
import CryptoJS from 'crypto-js'
import Timestamp from '@/components/Timestamp'
import UserIdentification from '@/components/UserIdentification'
import KBPreview from './KBPreview'
import KBPreviewList from './KBPreviewList.vue'
import { saveDivAsImage } from '../utils/helpers'
import { chatStore } from '../stores/chatStore'
import { memberStore } from '../stores/memberStore'

import { parseMarkup, parseStreamingMarkup } from '../utils/helpers'
import { submitBooleanRating, submitCommentRating } from '../api/chatDemoApi'

export default {
  name: 'ChatMessage',
  inject: ['memberStore', 'themeStore', 'partnerStore'],
  components: {
    ActionButton,
    RatingButton,
    Timestamp,
    KBPreview,
    KBPreviewList,
    UserIdentification,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    previousMessage: {
      type: Object,
      default: () => {
        return {}
      },
      required: false,
    },
  },
  data: function () {
    return {
      showSources: false,
      showRelatedLinks: false,
      userRating: null, // 'up' or 'down'
      ratingSubmitted: false,
      showFeedbackBox: false,
      feedbackComment: '',
      submittingFeedback: false
    }
  },
  methods: {
    async goToChatWithHuman() {
      const messageList = document.getElementById('message-list')
      messageList.style['padding-bottom'] = '60px'
      messageList.style['bottom'] = '0px'
      const chatFile = saveDivAsImage('message-list').then(dataUrl => {
        chatStore.storeBotChatImage(dataUrl)
      })
      messageList.style['padding-bottom'] = '0px'
      messageList.style['bottom'] = '61px'
      this.$router.push({ name: 'ChatWithHuman' })
    },
    goToImagePreview(url) {
      this.$router.push({
        name: 'ImagePreview',
        params: { url },
        query: { url },
      })
    },
    goToKB(id, $event) {
      $event.stopImmediatePropagation()
      this.$router.push({
        name: 'KBScreen',
        params: { id },
      })
    },
    onActionClick(action, message, $event) {
      console.log('action" ', action)
      console.log('message" ', message)
      console.log('$event" ', $event)
      let uri = action.actUri
      let resource = action.fromResource
      let commId = action.commId
      let messageId = action.messageId
      let messagebody = action.messageBody
      let uriData
      uri = decodeURI(uri)
      if (!~uri.indexOf('://')) {
        return
      }
      console.log('------>>> uri is ', uri)

      if (uri.startsWith('http')) {
        window.open(uri, '_blank')
      } else if (uri.startsWith('bt-msg://')) {
        if (uri.substr(9) && decodeURI(uri.substr(9))) {
          chatService.sendMessage(uri.substr(9))
        }
      } else if (uri.startsWith('bt-bot://')) {
        try {
          uriData = chatService.parseUri(uri)
          console.log('uriData: ', uriData)
          if (uriData.authority) {
            chatService.sendMessage(
              JSON.stringify({
                message: encodeURI(uri),
                secret: CryptoJS.MD5('data').toString(),
                channel: 'chat',
                context: {
                  source: messageId,
                },
              }),
            )
          }
        } catch (error) {
          console.log('error: ', error)
        }
      } else if (uri.startsWith('bt-data://')) {
        uriData = chatService.parseUri(uri)
        if (uriData.authority) {
          chatService.sendMessage(
            JSON.stringify({
              message: encodeURI(uri),
              secret: CryptoJS.MD5('data').toString(),
              channel: 'chat',
              context: {
                source: messageId,
              },
            }),
          )
        }
      } else if (uri.startsWith('bt-action://')) {
        uriData = chatService.parseUri(uri)
        if (uriData.authority) {
          // var commApi = new com.goboomtown.sdk.swagger.api.CommApi($http, $httpParamSerializer, $injector),
          // chatButtonCallback = commApi.chatButtonCallback({
          //     data: JSON.stringify(uriData.queryDict),
          //     fromResource: fromResource,
          //     commId: commId,
          //     messageId: messageId,
          //     messageBody: messageBody
          // });
          // chatButtonCallback.success(function (callbackResponse) {
          //     if (callbackResponse.success) {
          //         if (callbackResponse.uri) {
          //             me.handleActionUri(callbackResponse.uri, fromResource, commId, messageId,messageBody);
          //         }
          //     } else {
          //     }
          // }).error(function (callbackResponse) {
          // });
        }
      }
    },
    onRatingClick(action, message, $event) {
      this.onActionClick(action, message, $event)
    },
    toggleSources() {
      this.showSources = !this.showSources
    },
    toggleRelatedLinks() {
      this.showRelatedLinks = !this.showRelatedLinks
    },
    async onThumbsUp() {
      console.log('onThumbsUp clicked', {
        traceId: this.message.traceId,
        ratingSubmitted: this.ratingSubmitted,
        message: this.message
      })
      
      if (this.ratingSubmitted || !this.message.traceId) {
        console.warn('Cannot submit rating - already submitted or no traceId')
        return
      }
      
      this.userRating = 'up'
      this.ratingSubmitted = true
      
      try {
        // Get org_id from partnerStore config
        const userId = this.partnerStore.config.partnerTeam || 'unknown'
        console.log('Submitting thumbs up rating', { traceId: this.message.traceId, userId })
        await submitBooleanRating(this.message.traceId, true, userId)
        console.log('Thumbs up rating submitted successfully')
      } catch (error) {
        console.error('Error submitting thumbs up:', error)
        // Reset on error
        this.userRating = null
        this.ratingSubmitted = false
      }
    },
    async onThumbsDown() {
      console.log('onThumbsDown clicked', {
        traceId: this.message.traceId,
        ratingSubmitted: this.ratingSubmitted,
        message: this.message
      })
      
      if (this.ratingSubmitted || !this.message.traceId) {
        console.warn('Cannot submit rating - already submitted or no traceId')
        return
      }
      
      this.userRating = 'down'
      this.ratingSubmitted = true
      this.showFeedbackBox = true
      
      try {
        // Get org_id from partnerStore config
        const userId = this.partnerStore.config.partnerTeam || 'unknown'
        console.log('Submitting thumbs down rating', { traceId: this.message.traceId, userId })
        await submitBooleanRating(this.message.traceId, false, userId)
        console.log('Thumbs down rating submitted successfully')
      } catch (error) {
        console.error('Error submitting thumbs down:', error)
        // Don't reset - still show feedback box
      }
    },
    async submitFeedback() {
      if (!this.feedbackComment.trim() || this.submittingFeedback || !this.message.traceId) return
      
      this.submittingFeedback = true
      
      try {
        // Get org_id from partnerStore config
        const userId = this.partnerStore.config.partnerTeam || 'unknown'
        await submitCommentRating(this.message.traceId, this.feedbackComment, userId)
        
        // Close feedback box after successful submission
        this.showFeedbackBox = false
        this.feedbackComment = ''
      } catch (error) {
        console.error('Error submitting feedback:', error)
      } finally {
        this.submittingFeedback = false
      }
    },
    onAvatarError(e) {
      e.target.style.display = 'none'
    },
    sentAttachmentIcon(att) {
      if (att.type === 'url') return 'bt-icon-link'
      if (att.type === 'image') return 'bt-icon-image'
      return 'bt-icon-file'
    },
    getSourceUrl(source) {
      // Handle if source is an object with url property
      if (typeof source === 'object' && source.url) {
        return source.url
      }
      // Handle if source is a string
      if (typeof source === 'string') {
        return source
      }
      // Fallback
      return ''
    },
  },
  mounted() {
    window.setTimeout(() => {
      this.$refs.message && this.$refs.message.scrollIntoView()
    }, 150)
  },
  computed: {
    botAvatarUrl() {
      if (this.message.resource === 'ovation-bot') {
        return this.partnerStore.agentInfo.avatarUrl || null
      }
      return this.message.avatar || null
    },
    botAvatarInitial() {
      const name = this.message.resource === 'ovation-bot'
        ? this.partnerStore.agentInfo.name
        : this.message.name
      return name ? name.trim().charAt(0).toUpperCase() : '?'
    },
    hasContent() {
      return !!(this.message.message || this.message.streamingContent || this.message.hasImage || this.message.hasKb || this.message.hasAttachment || this.message.actions || this.message.ratingActions)
    },
    displayContent() {
      // Use streaming content if available, otherwise use final message
      const content = this.message.streamingContent || this.message.message
      
      // Use the same parser for both streaming and final content
      // This ensures consistent bullet formatting
      return parseMarkup(content)
    },
    parsedMessage() {
      if (this.message.resource === 'ovation-bot') {
        return parseMarkup(this.message.message)
      } else {
        return this.message.message
      }
    },
    fromUser() {
      if (this.message.resource.match('guest-')) {
        return true
      }

      if (this.message.resource === 'ovation-bot') {
        return false
      }

      if (
        this.message.resource.match(this.memberStore.member.members_users_id)
      ) {
        return true
      } else {
        return false
      }
    },
    showFromName() {
      // Always show name/avatar for the first message or if no previous message
      if (!this.previousMessage) {
        return true
      }
      
      // Always show if previous message is hidden
      if (this.previousMessage.hideMessage) {
        return true
      }
      
      // Always show if from different resources (different users/bots)
      if (this.message.resource !== this.previousMessage.resource) {
        return true
      }
      
      // For messages from the same resource, check timing
      if (this.message.timestamp && this.previousMessage.timestamp) {
        let diffInMilliseconds = Math.abs(
          new Date(this.message.timestamp) -
            new Date(this.previousMessage.timestamp),
        )
        let diffInSeconds = diffInMilliseconds / 1000
        
        // Show avatar if more than 2 minutes apart
        if (diffInSeconds >= 120) {
          return true
        }
        
        // For streaming messages, don't show avatar if previous message was also from bot and recent
        if (this.message.resource === 'ovation-bot' && this.previousMessage.resource === 'ovation-bot') {
          // Hide avatar for grouped bot messages
          return false
        }
        
        // Hide avatar for grouped messages (within 2 minutes)
        return false
      }
      
      // Default to showing avatar if we can't determine timing
      return true
    },
    iconClasses() {
      let baseClasses = 'file-icon bt-icon bt-icon-file'
      let specificIconClass =
        ' bt-icon-file-' + this.message.attachmentName.split('.')[1]
      if (specificIconClass) baseClasses += specificIconClass
      return baseClasses
    },
    fromDisplayName() {
      if (this.message.name?.match('guest-')) {
        return 'Guest'
      } else {
        return this.message.name
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.message {
  padding-right: 10px;
  padding-left: 36px;
  position: relative;
  margin-bottom: 4px;
}

/* right-aligned messges that come immediately after left-aligned messages */
.align-left + .align-right {
  margin-top: 10px;
}
/* left-aligned messges that come immediately after right-aligned messages */
.align-right + .align-left {
  margin-top: 10px;
}

.message.align-right {
  padding-left: 0px;
}

.avatar-wrapper {
  overflow: hidden;
  position: absolute;
  top: 28px;
  left: 0px;
  width: 32px;
  height: 32px;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #fff;
  background: #fff;
}

.avatar-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  user-select: none;
}

.message.show-avatar {
  padding-left: 36px;
}

.from-name {
  font-size: 11px;
  font-weight: bold;
  color: #97afc1;
  margin: 6px 0px;
}

.timestamp {
  font-weight: normal;
}

.align-right {
  text-align: right;
}

.message-text {
  background-color: #f3f8f8;
  border-radius: 6px;
  margin: 2px 0px 6px 0px;
  padding: 13px 15px;
  position: relative;
  word-break: break-word;
  font-size: 14px;
  line-height: 18px;
  min-width: auto;
  max-width: 82%;
  display: inline-block;
  color: #4f4f4f;
  text-align: left;

  &:deep(p) {
    margin-block-start: 0;
    margin-block-end: 0;
    line-height: 18px;
    
    &:not(:first-child) {
      margin-top: 6px;
    }
    &:not(:last-child) {
      margin-bottom: 6px;
    }
  }

  &:deep(ol),
  &:deep(ul) {
    padding-inline-start: 14px;
    margin: 6px 0;
    
    li {
      line-height: 20px;
      margin: 2px 0;
    }
  }
  
  // Headers need more line-height for readability
  &:deep(h1) {
    font-size: 24px;
    line-height: 30px;
    font-weight: bold;
    margin: 12px 0 8px 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  &:deep(h2) {
    font-size: 20px;
    line-height: 26px;
    font-weight: bold;
    margin: 10px 0 6px 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  &:deep(h3) {
    font-size: 16px;
    line-height: 22px;
    font-weight: bold;
    margin: 8px 0 4px 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  &:deep(h4),
  &:deep(h5),
  &:deep(h6) {
    font-size: 14px;
    line-height: 20px;
    font-weight: bold;
    margin: 6px 0 4px 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  // Code blocks
  &:deep(code) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 13px;
  }
  
  &:deep(pre) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 8px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 6px 0;
    
    code {
      background-color: transparent;
      padding: 0;
    }
  }
  
  // Blockquotes
  &:deep(blockquote) {
    border-left: 3px solid #6b737b;
    padding-left: 12px;
    margin: 6px 0;
    color: #6b737b;
  }
}

.align-right {
  .message-text {
    background-color: #dbf2f9;
  }
}

.image-wrapper {
  cursor: pointer;
}

.file-attachment {
  max-width: 65%;
  min-width: 30%;
  margin: 4px 0px;
  padding: 3px 0px 3px 0px;
  border-radius: 14px;
  background-color: white;
  display: inline-block;
}

.file-attachment-link {
  text-decoration: none;
}

.file-attachment-link:hover {
  .file-wrapper {
    transition: 250ms all linear;
    border: 1px solid #b6b6b6;
  }
}

.file-wrapper {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 12px;
  text-align: left;
}

.file-icon {
  font-size: 32px;
  margin-right: 10px;
  vertical-align: middle;
  color: #595959;
  margin-bottom: 3px;
}

.file-name {
  font-size: 13px;
  color: #4f4f4f;
  font-weight: 500;
}

.file-size {
  font-size: 12px;
  color: #acacac;
}

.bt-icon-file-pdf {
  color: #e41c13;
}

.bt-icon-file-xls {
  color: #089a53;
}

.bt-icon-file-doc {
  color: #1567d6;
}

.transfer-message {
  font-size: 12px;
  color: #4f4f4f;
  text-align: center;
  margin-top: 10px;
}

.streaming-cursor {
  animation: blink 1s infinite;
  font-weight: bold;
  color: #4f4f4f;
  margin-left: 2px;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

.message-footer {
  margin-top: 8px;
}

.rating-sources-container {
  display: flex;
  align-items: center;
  gap: 16px;
}

.rating-buttons {
  display: flex;
  gap: 8px;
}

.rating-btn {
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 16px;
  color: #6b737b;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    color: #4f4f4f;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  &.selected {
    color: #22a656;
  }
  
  &.selected-down {
    color: #e03131;
  }
  
  .bt-icon {
    font-size: 14px;
  }
}

.accordion {
  flex: 1;
}

.accordion-toggle {
  background: none;
  border: none;
  padding: 4px 0;
  text-align: left;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #6b737b;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #4f4f4f;
  }
}

.arrow {
  display: inline-block;
  margin-left: 4px;
  transition: transform 0.2s ease;
  font-size: 10px;
  
  &.arrow-down {
    transform: rotate(90deg);
  }
}

.accordion-content {
  padding: 8px 0;
  overflow: hidden;
  
  .source-item {
    margin-bottom: 4px;
    
    a {
      color: #4f4f4f;
      font-size: 12px;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 4px;
      
      &:hover {
        text-decoration: underline;
      }
      
      .bt-icon {
        font-size: 10px;
        opacity: 0.6;
      }
    }
  }
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  max-height: 300px;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 300px;
  opacity: 1;
}

.feedback-box {
  margin-top: 12px;
  padding: 12px;
  background: #f8fbfb;
  border-radius: 6px;
}

.feedback-textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #4f4f4f;
  }
  
  &::placeholder {
    color: #6b737b;
  }
}

.feedback-submit-btn {
  margin-top: 8px;
  padding: 6px 12px;
  background: #4f4f4f;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #3a3a3a;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .bt-icon {
    font-size: 12px;
  }
}

.fadeIn-enter-active,
.fadeIn-leave-active {
  transition: all 0.3s ease;
}

.fadeIn-enter-from,
.fadeIn-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

// Sent attachments on user messages
.sent-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
  justify-content: flex-end;
}

.sent-attachment-image {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;

  &:hover .sent-attachment-img {
    opacity: 0.85;
  }
}

.sent-attachment-img {
  width: 120px;
  height: 120px;
  object-fit: cover;
  display: block;
  border-radius: 8px;
  transition: opacity 150ms ease;
}

.sent-attachment-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  color: #4f4f4f;
  max-width: 200px;

  .align-right & {
    color: #4f4f4f;
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
  }

  .bt-icon {
    font-size: 11px;
    flex-shrink: 0;
  }
}

.sent-attachment-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
}

</style>
